import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Handles OAuth and magic link redirects.
 * Supabase JS client automatically picks up the tokens from the URL
 * via `detectSessionInUrl: true`, then onAuthStateChange fires in AuthContext.
 */
export function AuthCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    let handled = false;

    // Wait for auth state to settle, then redirect
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (handled) return;

      if (event === 'PASSWORD_RECOVERY') {
        handled = true;
        navigate('/reset-password', { replace: true });
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        handled = true;
        navigate('/dashboard', { replace: true });
      }
    });

    // Fallback: if already signed in or no callback params, redirect after short delay
    const timeout = setTimeout(() => {
      if (handled) return;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      });
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-600" />
        <p className="mt-4 text-text-secondary">{t('web:authCallbackPage.signingYouIn')}</p>
      </div>
    </div>
  );
}
